import { NamedElement, NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { SelectorRule } from '@fullstory/selectors';
import { PlatformSelector } from '@fullstory/proto/fs/api/segment';
import { Flags } from '@fullstory/client-api';

export enum NamedElementNameState {
  Empty,
  NameInUse,
  Valid,
}

export enum NamedElementSelectorState {
  Empty,
  Invalid,
  SelectorInUse,
  Valid,
  EmptyPlatforms,
}

export interface NamedElementValidation {
  readonly valid: boolean; // convenience rollup of underlying states
  readonly name: NamedElementNameState;
  readonly selectors: NamedElementSelectorState[];
}

// validateNamedElement provides validation for the provided named element.
// For the named element to be deemed valid, all resulting validation states must be marked valid.
export const validateNamedElement = (
  namedElement: NamedElement,
  unarchivedByName: Map<string, NamedElementRecord>,
  unarchivedBySelector: Map<string, NamedElementRecord>,
  flags: Flags,
): NamedElementValidation => {
  const nameState = validateNamedElementName(namedElement.name, namedElement.id, unarchivedByName);

  const selectorStates = validateNamedElementPlatformSelectors(
    namedElement.platformSelectors,
    namedElement.id,
    unarchivedBySelector,
  );

  return {
    valid:
      nameState === NamedElementNameState.Valid &&
      !selectorStates.some(ss => ss !== NamedElementSelectorState.Valid),
    name: nameState,
    selectors: selectorStates,
  };
};

// validateNamedElementName provides validation for the provided named element name and id.
// Validation will fail for empty names and names that are already in use.
export const validateNamedElementName = (
  name: string,
  id: string,
  unarchivedByName: Map<string, NamedElementRecord>,
): NamedElementNameState => {
  if (!name.trim()) {
    return NamedElementNameState.Empty;
  }
  if (unarchivedByName.has(name) && unarchivedByName.get(name)?.namedElement?.id !== id) {
    return NamedElementNameState.NameInUse;
  }
  return NamedElementNameState.Valid;
};

// validateNamedElementSelector provides validation for the provided named element selectors.
// Validation will fail for empty selectors, invalid selectors, and selectors that are already in use.
export const validateNamedElementSelectors = (
  selectors: string[],
  id: string,
  unarchivedBySelector: Map<string, NamedElementRecord>,
): NamedElementSelectorState[] => {
  if (!selectors.length) {
    return [NamedElementSelectorState.Empty];
  }
  return selectors.map(sel => validateSelector(sel, id, unarchivedBySelector));
};

function validateSelector(
  sel: string,
  id: string,
  unarchivedBySelector: Map<string, NamedElementRecord>,
): NamedElementSelectorState {
  if (!sel) {
    return NamedElementSelectorState.Empty;
  }
  if (!SelectorRule.isValid(sel)) {
    return NamedElementSelectorState.Invalid;
  }
  if (unarchivedBySelector.has(sel) && unarchivedBySelector.get(sel)?.namedElement?.id !== id) {
    return NamedElementSelectorState.SelectorInUse;
  }
  return NamedElementSelectorState.Valid;
}

export const validateNamedElementPlatformSelectors = (
  platformSelectors: PlatformSelector[],
  id: string,
  unarchivedBySelector: Map<string, NamedElementRecord>,
): NamedElementSelectorState[] => {
  if (!platformSelectors.length) {
    return [NamedElementSelectorState.Empty];
  }
  return platformSelectors.map(({ selector, platforms }) => {
    const valid = validateSelector(selector, id, unarchivedBySelector);
    if (!platforms.length && valid !== NamedElementSelectorState.Empty) {
      // Only show empty platforms error if the selector has a value.
      // Otherwise the error aggresively shows up before the user interacts with the form.
      return NamedElementSelectorState.EmptyPlatforms;
    }
    return valid;
  });
};
